import { useState } from "react";
import {
  Box,
  Paper,
  Title,
  TextInput,
  PasswordInput,
  Button,
  Divider,
  Anchor,
  Flex,
} from "@mantine/core";
import { IconMail, IconLock, IconShield, IconWorld, IconUsers } from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import trpc, { saveToken } from "../../shared/utils/trpc";
import { handleTrpcError } from "../../shared/utils/trpc/handletrpcError.util.ts";
import { successNotification } from "../../shared/utils/successNotification";
import { useUser } from "@/context/UserContext";
import { jwtDecode } from "jwt-decode";
import Cookies from "js-cookie";
import { GT_SIGNUP_CONTINUATION_KEY } from "../signup/signupFlowStorage";

function LoginPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");``
  const { refetchUser } = useUser();

  const token = Cookies.get("token");
  if (token) {
    try {
      const decoded = jwtDecode<{ role: string; exp: number }>(token);
      if (decoded.exp > Date.now() / 1000) {
        if (decoded.role === "ADMIN") {
          navigate("/admin/companies", { replace: true });
          return null;
        }
        navigate("/profile", { replace: true });
        return null;
      }
      Cookies.remove("token");
    } catch {
      Cookies.remove("token");
    }
  }

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isPasswordValid = password.length >= 8;
  const isFormValid = email.trim() !== "" && password.trim() !== "" && isValidEmail(email) && isPasswordValid;

  const handleLogin = async () => {
    if (!isFormValid) return;
    setLoading(true);
    try {
      const data = await trpc.auth.login.mutate({ email, password });
      if (data.signupContinuationToken) {
        sessionStorage.setItem(GT_SIGNUP_CONTINUATION_KEY, data.signupContinuationToken);
      }
      if (data.token) {
        saveToken(data.token);
        refetchUser();
      }
      successNotification({ title: "Login Successful!", message: "You have successfully logged in." });
      if (data.needsCompanyDetails && data.signupContinuationToken) {
        navigate("/signup/company-details", { replace: true });
        return;
      }
      navigate("/create-profile", { replace: true });
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box style={{ height: "100vh", display: "flex", overflow: "hidden" }}>
      {/* ── Left panel ── */}
      <Box
        style={{
          flex: "0 0 50%",
          position: "relative",
          overflow: "hidden",
        }}
        visibleFrom="md"
      >
        <img src="/LoginBackground.png" alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />

        {/* Text overlay */}
        <Box
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: "60px 56px",
          }}
        >
          <p style={{ fontSize: 36, fontWeight: 700, color: "#ffffff", marginBottom: 20, marginTop: 0 }}>
            Connect with Global Trade Partners
          </p>

          <p style={{ fontSize: 16, color: "rgba(255,255,255,0.8)", marginBottom: 48, marginTop: 0 }}>
            Join a trusted, verified network of international businesses. Discover opportunities, build partnerships, and
            grow your global reach.
          </p>

          <Flex direction="column" gap={20}>
            {[
              { icon: <IconShield size={18} />, text: "Verified business profiles you can trust" },
              { icon: <IconWorld size={18} />, text: "Access partners across 50+ countries" },
              { icon: <IconUsers size={18} />, text: "Collaborate securely with built-in NDA tools" },
            ].map(({ icon, text }) => (
              <Flex key={text} align="center" gap={14}>
                <Box
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: "#ffffff1a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#ffffff",
                    flexShrink: 0,
                  }}
                >
                  {icon}
                </Box>
                <span style={{ fontSize: 14, color: "rgba(255,255,255,0.8)" }}>{text}</span>
              </Flex>
            ))}
          </Flex>
        </Box>
      </Box>

      {/* ── Right panel ── */}
      <Box
        style={{
          flex: 1,
          background: "var(--mantine-color-white)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "48px 32px",
          overflowY: "auto",
        }}
      >
        <Box style={{ width: "100%", maxWidth: 360 }}>
          {/* Logo + subtitle */}
          <Flex direction="column" align="center" mb={32}>
            <img src="/gtLogo.svg" alt="GTsetu" style={{ height: 100, width: "auto", marginBottom: 8 }} />
          </Flex>

          {/* Form card */}
          <Paper withBorder p="xl" radius="lg" style={{ width: "100%" }}>
            <Title order={3} mb="lg">
              Log In
            </Title>

            <TextInput
              label="Email Address"
              placeholder="you@company.com"
              required
              mb="md"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              leftSection={<IconMail size={16} color="var(--mantine-color-slate-4)" />}
              error={email && !isValidEmail(email) ? "Please enter a valid email address" : null}
            />

            <PasswordInput
              label="Password"
              placeholder="Enter your password"
              required
              mb="md"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              leftSection={<IconLock size={16} color="var(--mantine-color-slate-4)" />}
              error={password && !isPasswordValid ? "Password must be at least 8 characters" : null}
            />

            <Flex justify="flex-end" mt={6} mb="md">
              <Anchor size="sm" component={Link} to="/forgot-password">
                Forgot password?
              </Anchor>
            </Flex>

            <Button fullWidth mb="md" onClick={handleLogin} loading={loading} disabled={!isFormValid || loading}>
              Log In
            </Button>

            <Divider label="New to GTsetu?" labelPosition="center" my="md" />

            <Button fullWidth variant="outline" component={Link} to="/signup">
              Create Account
            </Button>
          </Paper>

          {/* Back to home */}
          <Flex justify="center" mt="lg">
            <Anchor href="https://gtsetu.com/" size="sm" c="slate.5">
              ← Back to Home
            </Anchor>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
}

export default LoginPage;
